import { useEffect, useRef, useState } from 'react';
import { getMapboxToken, UM_MAPBOX_STYLES, type UMMapHandle } from '../_components';
import { createSatelliteStyle } from './satelliteStyle';
import type { BasemapId, SatelliteStatus } from './types';

export function useSatelliteMap() {
  const mapRef = useRef<UMMapHandle>(null);
  const token = getMapboxToken();
  const [status, setStatus] = useState<SatelliteStatus>(token ? 'loading' : 'offline');
  const [attempt, setAttempt] = useState(0);
  const [basemap, setBasemap] = useState<BasemapId>('light');
  // Checked = production Urban Map satellite style, unchanged.
  const [labels, setLabels] = useState(true);
  const pureSatellite = basemap === 'satellite' && !labels;

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    map.setMaxZoom(22);
    map.getCanvas().setAttribute('aria-label', 'Carte Urban Map : déplacement et zoom au clavier');
    if (!token) return;

    setStatus('loading');
    let failed = false;
    const timeout = window.setTimeout(() => {
      failed = true;
      setStatus('error');
    }, 25_000);
    const onError = () => {
      failed = true;
      window.clearTimeout(timeout);
      setStatus('error');
    };
    const onIdle = () => {
      if (!failed && map.isStyleLoaded() && map.areTilesLoaded()) {
        window.clearTimeout(timeout);
        setStatus('ready');
      }
    };
    map.on('error', onError);
    map.on('idle', onIdle);
    const style = pureSatellite
      ? createSatelliteStyle(token)
      : UM_MAPBOX_STYLES.find((item) => item.id === basemap)!.style;
    map.setStyle(style, { diff: false, localFontFamily: undefined, localIdeographFontFamily: undefined });
    return () => {
      window.clearTimeout(timeout);
      map.off('error', onError);
      map.off('idle', onIdle);
    };
  }, [token, attempt, basemap, pureSatellite]);

  return {
    mapRef,
    status,
    basemap,
    selectBasemap: (nextBasemap: BasemapId) => {
      if (nextBasemap === 'satellite') setLabels(true);
      setBasemap(nextBasemap);
    },
    labels,
    setLabels,
    hasToken: !!token,
    retry: () => setAttempt((value) => value + 1),
  };
}
